import { Boxes } from "@/components/ui/aceternity-ui/background-boxes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import { SparklesCore } from "@/components/ui/sparkles";
import { Timeline } from "@/components/ui/timeline";
import { cn } from "@/lib/utils";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Brain,
  CalendarDays,
  Code,
  Cpu,
  Database,
  Lightbulb,
  LineChart,
  RocketIcon,
  Shield,
  Target,
  Users,
  WrenchIcon,
  Zap,
} from "lucide-react";
import { useRef } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const stats = [
  {
    icon: CalendarDays,
    label: "Year founded",
    value: "2025",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
  },
  {
    icon: Cpu,
    label: "AI models integrated",
    value: "33+",
    color: "text-green-500",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
  },
  {
    icon: Users,
    label: "Team members",
    value: "3",
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
  },
  {
    icon: Database,
    label: "Hosted Tools",
    value: "60+",
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
  },
];

const values = [
  {
    icon: Shield,
    title: "Trust & Security",
    description:
      "We place the highest priority on user data protection and privacy.",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description:
      "We constantly push boundaries to create the best AI experiences.",
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10",
    borderColor: "border-yellow-500/30",
  },
  {
    icon: Target,
    title: "User-Centered",
    description: "Every feature we build starts with user needs and feedback.",
    color: "text-green-500",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
  },
  {
    icon: Zap,
    title: "Efficiency",
    description:
      "We help users work smarter, not harder, with powerful AI tools.",
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
    borderColor: "border-orange-500/30",
  },
  {
    icon: Brain,
    title: "Thoughtful AI",
    description:
      "We develop AI that enhances human capabilities without replacing them.",
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
  },
  {
    icon: LineChart,
    title: "Continuous Improvement",
    description:
      "We're committed to constant learning and iterative enhancement.",
    color: "text-pink-500",
    bgColor: "bg-pink-500/10",
    borderColor: "border-pink-500/30",
  },
];

const timelineData = [
  {
    title: "Our Beginning",
    content: (
      <div className="bg-card rounded-xl p-6 shadow-lg">
        <h4 className="text-xl font-semibold mb-3">Foundation</h4>
        <p className="text-muted-foreground mb-4">
          Artifact began with a simple mission: to make AI accessible and useful
          for everyone. Our journey started with a small team of passionate
          engineers and designers.
        </p>
        <div className="grid grid-cols-2 gap-1">
          <img
            src="/solutions/reed-image.png"
            className="rounded-lg object-cover h-48"
            alt="Reed - Co-founder"
          />
          <img
            src="/solutions/jack-image.jpeg"
            className="rounded-lg object-cover h-48"
            alt="Jack - Co-founder"
          />
        </div>
      </div>
    ),
  },
  {
    title: "Product Launch",
    content: (
      <div className="bg-card rounded-xl p-6 shadow-lg">
        <h4 className="text-xl font-semibold mb-3">Initial Product</h4>
        <p className="text-muted-foreground mb-4">
          After months of development, we launched our first public beta,
          allowing users to experience AI-powered productivity tools firsthand.
        </p>
        <div className="flex justify-center">
          <img
            src="/solutions/product.png"
            className="rounded-lg max-w-full h-auto"
            alt="Product Launch"
          />
        </div>
      </div>
    ),
  },
  {
    title: "The Future",
    content: (
      <div className="bg-card rounded-xl p-6 shadow-lg">
        <h4 className="text-xl font-semibold mb-3">Our Roadmap</h4>
        <p className="text-muted-foreground mb-4">
          We&#39;re constantly evolving and have big plans for the future of
          Artifact. Our roadmap includes more integrations, customized AI
          experiences, and tools that make work delightful.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-full bg-blue-500/20">
                <Code className="h-5 w-5 text-blue-500" />
              </div>
              <h5 className="font-semibold ml-3">Fully Agentic Chat</h5>
            </div>
            <p className="text-sm text-muted-foreground">
              AI agents that can autonomously execute complex tasks, make
              decisions, and interact with multiple tools seamlessly.
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-full bg-purple-500/20">
                <WrenchIcon className="h-5 w-5 text-purple-500" />
              </div>
              <h5 className="font-semibold ml-3">Extensive Tool Library</h5>
            </div>
            <p className="text-sm text-muted-foreground">
              A comprehensive collection of Model Context Protocol tools for
              enhanced AI capabilities and integrations.
            </p>
          </div>
          <div className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border border-pink-500/20 rounded-lg p-4">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-full bg-pink-500/20">
                <RocketIcon className="h-5 w-5 text-pink-500" />
              </div>
              <h5 className="font-semibold ml-3">Live Editing Canvas Mode</h5>
            </div>
            <p className="text-sm text-muted-foreground">
              Real-time collaborative editing with visual canvas interface for
              dynamic content creation and manipulation.
            </p>
          </div>
        </div>
      </div>
    ),
  },
];

export default function AboutPage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const translateY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <>
      <Helmet>
        <title>About Us | Artifact - AI Enhanced Productivity</title>
        <meta
          name="description"
          content="Learn about the Artifact team and our mission to enhance human productivity with AI tools."
        />
      </Helmet>

      <div className="flex flex-col min-h-screen">
        {/* Hero Section with Background Boxes */}
        <section className="relative py-20 overflow-hidden h-[90vh] flex items-center justify-center">
          <div className="absolute inset-0 w-full h-full">
            <Boxes />
            <div className="absolute inset-0 w-full h-full bg-background/80 z-10" />
            <SparklesCore
              id="hero-sparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={40}
              speed={0.5}
              particleColor="#8b5cf6"
              className="w-full h-full opacity-50 z-20"
            />
          </div>

          <div className="container mx-auto px-4 relative z-30">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h1
                className="text-5xl md:text-6xl lg:text-7xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-blue-500"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                About Artifact
              </motion.h1>
              <motion.div
                className="text-xl md:text-2xl text-muted-foreground mb-10 leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <p>
                  We&#39;re building a platform that helps people think, create,
                  and collaborate with AI. Artifact transforms the way you work
                  with documents, conversations, and knowledge.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-wrap gap-4 justify-center"
              >
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white hover:text-white/95 rounded-full px-8 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <Link to="/chat">Try Artifact</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full border-purple-300 dark:border-purple-700 px-8 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-800 dark:hover:text-purple-200 hover:border-purple-400 dark:hover:border-purple-600 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <Link to="/solutions">View Solutions</Link>
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30"
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <svg className="w-6 h-10 text-muted-foreground" viewBox="0 0 24 40">
              <rect
                x="10"
                y="0"
                width="4"
                height="40"
                rx="2"
                fill="currentColor"
                fillOpacity="0.3"
              />
              <rect
                x="10"
                y="0"
                width="4"
                height="10"
                rx="2"
                fill="currentColor"
              />
            </svg>
          </motion.div>
        </section>

        {/* Stats Section using Shadcn Cards */}
        <section
          className="py-12 bg-gradient-to-b from-background via-background/95 to-background"
          ref={ref}
        >
          <div className="container mx-auto px-4">
            <motion.h2
              className="text-3xl md:text-5xl font-bold text-center mb-12"
              style={{ y: translateY }}
            >
              By the Numbers
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    className={cn(
                      "h-full shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden",
                      stat.borderColor
                    )}
                  >
                    <CardHeader className={cn("pb-2", stat.bgColor)}>
                      <div className="flex items-center gap-3">
                        <div className={cn("p-3 rounded-full", stat.bgColor)}>
                          <stat.icon className={cn("w-5 h-5", stat.color)} />
                        </div>
                        <CardTitle className="text-lg">{stat.label}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      <p className="text-4xl font-bold text-center">
                        {stat.value}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Statement using Container Scroll Animation */}
        <ContainerScroll
          titleComponent={
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4">
              Our Mission
            </h2>
          }
        >
          <div className="h-full w-full rounded-2xl flex items-center justify-center bg-gradient-to-br from-purple-500/20 via-background to-blue-500/20 p-10">
            <div className="max-w-2xl text-center">
              <h3 className="text-2xl md:text-3xl font-semibold mb-6">
                We&#39;re building{" "}
                <span className="text-primary">Artifact</span> because we
                believe in you and your creations.
              </h3>
              <p className="text-xl text-muted-foreground mb-8">
                We believe in using AI to amplify human creativity and
                productivity, not replace it. Our mission is to create tools
                that help people work smarter, think deeper, and create more
                effectively with the power of artificial intelligence.
              </p>
              <p className="text-lg text-muted-foreground">
                We want to help you transform the way you work with documents,
                conversations, and knowledge into beautiful presentations,
                websites, social posts — and anything else you can dream.
              </p>
            </div>
          </div>
        </ContainerScroll>

        {/* Values Section as Grid */}
        <section className="py-12 bg-gradient-to-b from-background to-background/90">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
              Our Values
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    className={cn(
                      "h-full shadow-md hover:shadow-lg transition-all duration-300",
                      value.borderColor
                    )}
                  >
                    <CardHeader>
                      <div
                        className={cn(
                          "p-3 rounded-full mb-3 w-fit",
                          value.bgColor
                        )}
                      >
                        <value.icon className={cn("w-6 h-6", value.color)} />
                      </div>
                      <CardTitle>{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16 bg-gradient-to-b from-background/90 to-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-12">
              Our Journey
            </h2>
            <Timeline data={timelineData} />
          </div>
        </section>

        {/* CTA Section with Glow Effect */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 blur-3xl opacity-70"></div>
          </div>

          <div className="container max-w-4xl mx-auto px-8 md:px-12 lg:px-16 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-blue-500">
                Ready to transform how you work with AI?
              </h2>
              <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
                Experience the power of Artifact&#39;s intelligent assistant for
                your productivity workflow. Join thousands of users who have
                already discovered a better way to work.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white hover:text-white/95 rounded-full px-10 py-6 shadow-md hover:shadow-lg transition-all duration-300 text-lg"
                >
                  <Link to="/chat">Get Started Today</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full border-purple-300 dark:border-purple-700 px-10 py-6 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-800 dark:hover:text-purple-200 hover:border-purple-400 dark:hover:border-purple-600 transition-all duration-300 shadow-sm hover:shadow-md text-lg"
                >
                  <Link to="/integrations">Explore Our Integrations</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
